import { ReactNode, useId, useState } from 'react';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';

import type { LicenseBalance } from '../api/licensing';
import type { MintReadiness } from '../api/waves';
import type { ActivationCheck, ActivationChecks } from '../lib/activationReadiness';
import { errorText } from '../lib/errorText';
import { IDENTIFIER, PLAIN_BUTTON, RADIUS, STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

import { CheckStatusIcon } from './ActivationReadinessSummary';
import LicenseBalancePanel from './LicenseBalancePanel';

// FD-611 — every diagnostic the activation dialog used to show inline, MOVED here
// behind one disclosure, collapsed by default. FD-612 renamed it "Technical details
// (N checks)" and gave it the same bordered-row disclosure as "Advanced sign-in
// options", with the checklist opening inside the same box rather than a second one.
// Nothing was dropped on the way in: the minting-account panel's wording (FD-451,
// FD-450, FD-609, FD-592) and the licence balance (FD-435) are carried verbatim, so
// an operator who opens this sees exactly what they saw before, in one checklist.
//
// The checklist rows come from `deriveActivationChecks` — the same rows the summary
// counts — so the number in the disclosure's label is the number of rows below it.

function Row({ check, children }: { check: ActivationCheck; children?: ReactNode }) {
  return (
    <div className="grid grid-cols-[20px_minmax(110px,160px)_1fr] items-start gap-x-2 gap-y-0.5 text-[12.5px]">
      <span className="mt-px">
        <CheckStatusIcon status={check.status} size={15} />
      </span>
      <span style={{ color: TEXT.secondary }}>{check.label}</span>
      <span style={{ color: TEXT.primary }} className="min-w-0 break-words">
        {children ?? check.value}
        {check.hint && (
          <span style={{ color: TEXT.secondary }}> — {check.hint}</span>
        )}
      </span>
    </div>
  );
}

/** The minting-account half, under the testid the FD-451 suites already address. */
function MintingAccountChecks({
  checks,
  readiness,
  isLoading,
  error,
}: {
  checks: ActivationChecks;
  readiness: MintReadiness | null | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading) {
    return (
      <div className="text-[12.5px]" data-testid="mint-readiness">
        <span style={{ color: TEXT.secondary }}>Checking the minting account…</span>
      </div>
    );
  }

  // A failed check is NOT a failed activation. It says so, and gets out of the way.
  if (error || !readiness) {
    return (
      <div className="text-[12.5px]" data-testid="mint-readiness">
        <span style={{ color: TEXT.secondary }}>
          Could not check the minting account ({errorText(error)}). This does not block activation —
          continue, and the sign-in itself will report any problem.
        </span>
      </div>
    );
  }

  const { account, checked, serviceAccountUpn, serviceAccountSource, tenantDomain } = readiness;
  const nothingResolved = serviceAccountSource === 'none' || !serviceAccountUpn;
  const failed = checked && account ? !account.present || !account.role || account.caExclusionHealthy === false : false;
  const verdict = nothingResolved ? 'not configured' : failed ? 'needs attention' : checked ? 'checked' : 'not checked';
  const tone = nothingResolved || failed ? STATUS.warningText : checked ? STATUS.successText : TEXT.secondary;

  const byId = (id: ActivationCheck['id']) => checks.rows.find((r) => r.id === id);
  const accountRow = byId('account');

  return (
    <div className="flex flex-col gap-2" data-testid="mint-readiness">
      <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
        <span className="font-medium" style={{ color: TEXT.primary }}>
          Minting account
        </span>
        <span className="text-[11px]" style={{ color: tone }}>
          {verdict}
        </span>
      </div>

      {nothingResolved ? (
        <span className="text-[12.5px]" style={{ color: STATUS.warningText }}>
          No account is recorded for {tenantDomain ?? 'this destination tenant'}. Set it on Tenants ›
          Service accounts — signing in as the wrong account is <code>AADSTS240005</code>.
        </span>
      ) : (
        <>
          {accountRow && (
            <Row check={accountRow}>
              <span className="flex flex-col">
                <span style={IDENTIFIER}>{serviceAccountUpn}</span>
                {/* FD-450: a deployment-wide default and a per-tenant value looked
                    identical while meaning entirely different things. */}
                <span className="text-[11.5px]" style={{ color: TEXT.secondary }}>
                  {serviceAccountSource === 'tenant'
                    ? `recorded for ${tenantDomain ?? 'this tenant'}`
                    : 'deployment default'}
                </span>
              </span>
            </Row>
          )}
          {(['exists', 'role', 'conditional-access', 'account-checks'] as const).map((id) => {
            const row = byId(id);
            return row ? <Row key={id} check={row} /> : null;
          })}
          {byId('device-code') && (
            <Row check={byId('device-code')!}>
              <span data-testid="device-code-line">{byId('device-code')!.value}</span>
            </Row>
          )}
        </>
      )}

      {/* The caveats travel with the verdict. A green line here is not proof. */}
      {checked && readiness.caveats.length > 0 && (
        <ul className="m-0 flex list-disc flex-col gap-0.5 pl-9 text-[11px]" style={{ color: TEXT.secondary }}>
          {readiness.caveats.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface ActivationTechnicalDetailsProps {
  checks: ActivationChecks;
  readiness: MintReadiness | null | undefined;
  readinessLoading: boolean;
  readinessError: unknown;
  balance: LicenseBalance | null | undefined;
  balanceLoading: boolean;
  balanceError: unknown;
  reserveCount: number | null;
}

export default function ActivationTechnicalDetails(props: ActivationTechnicalDetailsProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const { checks } = props;
  const licence = checks.rows.find((r) => r.id === 'licence');
  const n = checks.rows.length;

  return (
    <section
      style={{
        border: `1px solid ${STROKE.base}`,
        borderRadius: RADIUS.lg,
        backgroundColor: SURFACE.base,
        overflow: 'hidden',
      }}
      data-testid="technical-details-section"
    >
      <button
        type="button"
        className={`${PLAIN_BUTTON} flex w-full items-center gap-3 px-3 py-2.5 text-left focus-visible:outline focus-visible:outline-2`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        style={{ cursor: 'pointer' }}
      >
        <FileText size={18} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-[13.5px]" style={{ color: TEXT.primary }}>
            <span className="font-semibold">Technical details</span>{' '}
            <span style={{ color: TEXT.secondary }}>
              ({n} {n === 1 ? 'check' : 'checks'})
            </span>
          </span>
          <span className="text-[12px]" style={{ color: TEXT.secondary }}>
            Account, permissions, license capacity and Conditional Access details
          </span>
        </span>
        {open ? (
          <ChevronUp size={16} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
        ) : (
          <ChevronDown size={16} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
        )}
      </button>

      {open && (
        <div
          id={panelId}
          className="flex flex-col gap-3 px-4 py-3"
          style={{ borderTop: `1px solid ${STROKE.subtle}`, backgroundColor: SURFACE.rowFill }}
          data-testid="technical-details"
        >
          <MintingAccountChecks
            checks={checks}
            readiness={props.readiness}
            isLoading={props.readinessLoading}
            error={props.readinessError}
          />
          {licence && (
            <Row check={licence}>
              <LicenseBalancePanel
                variant="inline"
                reserveCount={props.reserveCount}
                balance={props.balance}
                isLoading={props.balanceLoading}
                error={props.balanceError}
              />
            </Row>
          )}
        </div>
      )}
    </section>
  );
}
